import React, { useState, useEffect, useContext } from "react";
import { useParams } from "react-router-dom";
import Leftsidebar from "../Sidebar/Sidebar.js";
import InfiniteScroll from "react-infinite-scroll-component";
import QuestionItem from "./QuestionItem.js";
import AnswerItem from "./AnswerItem.js";
import CreatePostForm from "./CreatePostForm.js";
import { backend_url } from "shared/config.js";
import { AuthContext } from "../Auth/AuthProvider.js";

const QuestionPage = () => {
  const { user, userInfo, accessToken } = useContext(AuthContext);

  const [answersAndCreators, setAnswersAndCreators] = useState([]);
  const [postCreator, setPostCreator] = useState(null);
  const [post, setPost] = useState(null);
  const { postId } = useParams();
  const [hasMorePosts, setHasMorePosts] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    fetch(backend_url + "/api/question_page_from_id?post_id=" + postId, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: user?.uid ?? "",
      },
    })
      .then((res) => res.json())
      .then((data) => {
        setPostCreator(data.postCreator);
        setPost(data.question);
        setAnswersAndCreators(data.answers);
        setIsLoading(false);
        return data;
      })
      .catch((error) => {
        console.error("Error retrieving post or children", error);
        setIsLoading(false);
      });
  }, [postId, user]);

  const handleSubmitAnswer = async (submissionData) => {
    try {
      const response = await fetch(
        `${backend_url}/create/answer_and_ai_response`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer: " + accessToken,
          },
          body: JSON.stringify(submissionData),
        }
      );

      if (response.ok) {
        const response_data = (await response.json()).data;

        setAnswersAndCreators((answersAndCreators) => [
          ...response_data, // Spread the array of new posts
          ...answersAndCreators,
        ]);

        return true; // Success - allows form to reset
      } else {
        const errorData = await response.json();
        console.error("Error creating reply:", errorData.error);
        return false; // Failure - prevents form reset
      }
    } catch (error) {
      console.error("Error creating reply:", error);
      return false; // Failure - prevents form reset
    }
  };

  const handleDeletePost = (postId) => {
    setAnswersAndCreators((prevItems) =>
      prevItems.filter((item) => item.answer.id !== postId)
    );
  };

  const handleAIResponseGenerated = (aiResponseData) => {
    console.log("AI Response Data:", aiResponseData);

    // Check if we have the expected structure from the AI controller
    if (aiResponseData && aiResponseData.answer && aiResponseData.creator) {
      // This matches the structure returned by the AI controller
      setAnswersAndCreators((prevItems) => [aiResponseData, ...prevItems]);
    } else {
      console.error("Invalid AI response data structure:", aiResponseData);
    }
  };

  return (
    <div className="flex flex-wrap w-full h-full">
      <div className="flex flex-row w-full bg-gray-100">
        <Leftsidebar />

        <main className="flex flex-col grow p-6 overflow-x-hidden overflow-y-auto">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
              <p className="mt-4 text-gray-600">Loading content...</p>
            </div>
          ) : (
            <>
              <QuestionItem
                key={postId}
                post={post}
                postCreator={postCreator}
                reply_page={true}
              />

              {user ? (
                <CreatePostForm
                  onSubmit={handleSubmitAnswer}
                  user={user}
                  formType={CreatePostForm.FORM_TYPES.ANSWER}
                  parentId={postId}
                  placeholder="Share your answer... (Markdown supported)"
                  showAdvanced={true}
                  isTopLevel={true}
                  className="mb-6"
                />
              ) : (
                <div className="text-center text-gray-500 mb-6">
                  <p className="text-center text-gray-500">
                    Please log in to participate.
                  </p>
                </div>
              )}

              <InfiniteScroll
                dataLength={answersAndCreators.length}
                hasMore={hasMorePosts}
                loader={<h4>Loading...</h4>}
                endMessage={
                  <p style={{ textAlign: "center" }}>
                    <b>No more answers to show</b>
                  </p>
                }
                className="space-y-4"
              >
                <div className="space-y-4">
                  {answersAndCreators?.map((item) => {
                    // Add safety check to prevent the error
                    if (!item || !item.answer) {
                      console.warn("Invalid item in answersAndCreators:", item);
                      return null;
                    }

                    return (
                      <AnswerItem
                        key={item.answer.id}
                        item={item}
                        post={item.answer}
                        postCreator={item.creator}
                        onDelete={handleDeletePost}
                      />
                    );
                  })}
                </div>
              </InfiniteScroll>
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default QuestionPage;
